import { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Dropdown,
  Empty,
  List,
  Space,
  Spin,
  Typography,
  message,
  theme,
} from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/api';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';

const { Text } = Typography;

type GeneralNotificationItem = {
  id: string;
  announcementId: string | null;
  title: string;
  bodyMd: string | null;
  status: 'unread' | 'read' | 'dismissed';
  createdAt: string;
  metadata: Record<string, unknown> | null;
  announcement?: {
    priority: string | null;
  } | null;
};

type CombinedNotification = {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  unread: boolean;
  source: 'general';
  priority?: string | null;
  targetPath: string;
};

const toPlainText = (markdown: string | null | undefined): string => {
  if (!markdown) {
    return '';
  }

  return markdown
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/[-#*_>~]+/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/\n+/g, ' ')
    .trim();
};

export function NotificationBell() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { effectiveTheme } = useUserPreferences();
  const [open, setOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const utils = trpc.useContext();
  const isDarkTheme = effectiveTheme === 'dark';

  const {
    data: generalData,
    isLoading: generalLoading,
    isFetching: generalFetching,
    refetch: generalRefetch,
  } = trpc.notifications.list.useQuery(
    { limit: 10 },
    { staleTime: 30_000, refetchOnWindowFocus: false },
  );

  const generalMarkMutation = trpc.notifications.markAsRead.useMutation({
    onSuccess: async () => {
      await utils.notifications.list.invalidate();
    },
    onError: (error) => {
      messageApi.error(error.message ?? '通知の更新に失敗しました');
    },
  });

  const generalDeleteMutation = trpc.notifications.delete.useMutation({
    onSuccess: async () => {
      await utils.notifications.list.invalidate();
    },
    onError: (error) => {
      messageApi.error(error.message ?? '通知の削除に失敗しました');
    },
  });

  const generalMarkAllMutation = trpc.notifications.markAllAsRead.useMutation({
    onSuccess: async () => {
      await utils.notifications.list.invalidate();
      messageApi.success('共通通知をすべて既読にしました');
    },
    onError: (error) => {
      messageApi.error(error.message ?? '通知の更新に失敗しました');
    },
  });

  const totalUnreadCount = generalData?.unreadCount ?? 0;

  const combinedItems = useMemo<CombinedNotification[]>(() => {
    return ((generalData?.items as GeneralNotificationItem[] | undefined) ?? [])
      .map((item) => ({
        id: item.id,
        title: item.title,
        description: toPlainText(item.bodyMd)?.slice(0, 140) ?? '',
        createdAt: item.createdAt,
        unread: item.status === 'unread',
        source: 'general' as const,
        priority: item.announcement?.priority ?? null,
        targetPath: item.announcementId ? '/app/announcements' : '/app/notifications',
      }))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [generalData?.items]);

  const handleMarkRead = useCallback(
    async (item: CombinedNotification) => {
      if (!item.unread) {
        return;
      }

      await generalMarkMutation.mutateAsync({ notificationId: item.id });
    },
    [generalMarkMutation],
  );

  const handleDelete = useCallback(
    async (item: CombinedNotification) => {
      await generalDeleteMutation.mutateAsync({ notificationId: item.id });
    },
    [generalDeleteMutation],
  );

  const handleNavigate = useCallback(
    (item: CombinedNotification) => {
      setOpen(false);
      navigate(item.targetPath);
    },
    [navigate],
  );

  const dropdownContent = useMemo(() => {
    const loading = generalLoading || generalFetching;

    if (loading) {
      return (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: isDarkTheme ? 'rgba(255, 255, 255, 0.85)' : token.colorText,
          }}
        >
          <Spin />
        </div>
      );
    }

    if (combinedItems.length === 0) {
      return (
        <div
          style={{
            padding: 24,
            color: isDarkTheme ? 'rgba(255, 255, 255, 0.65)' : token.colorTextSecondary,
          }}
        >
          <Empty description="通知はありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    }

    return (
      <List
        style={{ width: 320, maxHeight: 400, overflowY: 'auto' }}
        dataSource={combinedItems}
        renderItem={(item) => {
          const markLoading = generalMarkMutation.isPending;
          const deleteLoading = generalDeleteMutation.isPending;

          return (
            <List.Item
              key={`${item.source}-${item.id}`}
              style={{
                paddingInline: 12,
                paddingBlock: 8,
                background: item.unread
                  ? (isDarkTheme ? 'rgba(22, 119, 255, 0.2)' : token.colorPrimaryBg)
                  : (isDarkTheme ? '#1f1f1f' : token.colorBgContainer),
                cursor: 'pointer',
              }}
              role="button"
              tabIndex={0}
              onClick={() => handleNavigate(item)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleNavigate(item);
                }
              }}
              actions={[
                <Button
                  key="mark"
                  type="text"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  disabled={!item.unread || markLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleMarkRead(item);
                  }}
                  title="既読にする"
                  style={{ padding: '4px 8px' }}
                />,
                <Button
                  key="delete"
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={deleteLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleDelete(item);
                  }}
                  title="削除"
                  style={{ padding: '4px 8px' }}
                />,
              ]}
            >
              <List.Item.Meta
                style={{ alignItems: 'flex-start' }}
                title={
                  <Space size={4} wrap style={{ maxWidth: 200 }}>
                    {item.unread && <Badge status="processing" />}
                    <Text
                      strong={item.unread}
                      style={{
                        fontSize: 14,
                        color: isDarkTheme ? 'rgba(255, 255, 255, 0.85)' : token.colorText,
                      }}
                    >
                      {item.title}
                    </Text>
                  </Space>
                }
                description={
                  <Text
                    style={{
                      fontSize: 11,
                      color: isDarkTheme ? 'rgba(255, 255, 255, 0.65)' : token.colorTextSecondary,
                    }}
                  >
                    {new Date(item.createdAt).toLocaleString('ja-JP', {
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                }
              />
            </List.Item>
          );
        }}
      />
    );
  }, [
    combinedItems,
    generalDeleteMutation.isPending,
    generalFetching,
    generalLoading,
    generalMarkMutation.isPending,
    handleDelete,
    handleMarkRead,
    handleNavigate,
    isDarkTheme,
    token.colorBgContainer,
    token.colorPrimaryBg,
    token.colorText,
    token.colorTextSecondary,
  ]);

  const dropdownHeader = (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.12)' : token.colorBorderSecondary}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <Space direction="vertical" size={2}>
        <Space>
          <BellOutlined />
          <Text strong>通知</Text>
        </Space>
        <Text type="secondary" style={{ fontSize: 12 }}>
          未読: {totalUnreadCount}件
        </Text>
      </Space>
      <Button
        type="link"
        size="small"
        onClick={() => void generalMarkAllMutation.mutateAsync()}
        disabled={generalMarkAllMutation.isPending || totalUnreadCount === 0}
      >
        すべて既読
      </Button>
    </div>
  );

  const menu: MenuProps = {
    items: [],
  };

  return (
    <>
      {contextHolder}
      <Dropdown
        menu={menu}
        open={open}
        trigger={['click']}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) {
            void generalRefetch();
          }
        }}
        popupRender={() => (
          <div
            style={{
              background: isDarkTheme ? '#141414' : token.colorBgElevated,
              color: isDarkTheme ? 'rgba(255, 255, 255, 0.85)' : token.colorText,
              borderRadius: 8,
              boxShadow: token.boxShadowSecondary,
              overflow: 'hidden',
              width: 320,
              minWidth: 320,
              maxWidth: 320,
            }}
          >
            {dropdownHeader}
            {dropdownContent}
          </div>
        )}
      >
        <Badge count={totalUnreadCount} size="small">
          <Button
            type="text"
            icon={<BellOutlined />}
            aria-label="通知"
          />
        </Badge>
      </Dropdown>
    </>
  );
}
